import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import fileDownload from 'js-file-download';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Download the canonical import template.
 *
 * Until automatic column reading ships, this IS the path for an unrecognised
 * layout: fill the template, upload it, and it resolves from the seeded global
 * mapping profile with zero inference. Disabled by default — trigger via
 * refetch().
 */
const endpoint = `${priceImportsEndpointIdentifier}/template/download`;
export const downloadPriceImportTemplateEndpointIdentifier = endpoint;

type TData = void;
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<void, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useDownloadPriceImportTemplateApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const queryFn = async () => {
    try {
      const response = await http.get(endpoint, { responseType: 'arraybuffer' });

      fileDownload(response.data, 'kaso-price-list-template.xlsx');
    } catch (error: any) {
      // Important to not show the toast in unauthorized case.
      if (!error.response?.status || error.response?.status === 401) throw error;

      // responseType is arraybuffer, so error.response.data is binary.
      toast({ description: 'Failed to download the price list template', status: 'error' });

      throw error;
    }
  };

  return useQueryWrapper([endpoint], queryFn, { enabled: false, ...options });
}
